import { useCallback, useEffect, useRef, useState } from 'react'
import type { Side } from '../../shared/espn'
import { gameClosed } from '../../shared/visibility'
import { ApiError, api, type State } from '../api'
import { browserStore, confirmed, enqueue, loadQueue, mergePicks, permanent, removeSynced, saveQueue, type Pending } from '../queue'
import { Empty, GameCard, WeekDates } from '../ui'

/*
 * The board.
 *
 * THE NETWORK IS NOT IN THE PATH OF A TAP. Measured against the live site, the
 * first version cost 300-490ms before anything appeared on screen - a POST and
 * then a full refetch of the entire state to learn one fact it had just
 * decided itself. On a phone on bad wifi that is seconds, or the tap vanishes.
 *
 * So a tap now writes to local state and to storage, and paints immediately. A
 * background drain posts what is pending and retries what fails. A pick made in
 * a dead spot is still there when the phone finds signal, and a refetch that
 * lands mid-tap cannot flip a row back under the player's thumb, because
 * pending picks win over the server's older answer.
 *
 * "Lock it in" shuts your own cards until you reopen them - it used to only
 * signal, which let a locked board be changed with a single tap. It is still
 * not the real lock: that is the board's deadline, the first kickoff, on the
 * server's clock, and nobody can reopen past it.
 */
export function Picks({ state, tz, onChanged }: { state: State; tz: string; onChanged: () => void }) {
  const store = useRef(browserStore()).current
  const [queue, setQueue] = useState<Pending[]>(() => loadQueue(store))
  const [offline, setOffline] = useState(false)
  /** What the server said when it refused a pick outright. See the drain. */
  const [refused, setRefused] = useState<string | null>(null)
  /* Sent, accepted, and not yet reflected in the state we have. See mergePicks. */
  const [sent, setSent] = useState<Pending[]>([])
  /*
   * The lock as tapped, until the server's answer catches up. Without it the
   * cards stay live for the round trip after "Lock it in", which is exactly
   * the window a second tap lands in.
   */
  const [heldLocal, setHeldLocal] = useState<boolean | null>(null)
  const draining = useRef(false)

  const me = state.me.playerId

  /*
   * Drain what is pending, one at a time, oldest first.
   *
   * A failure leaves the item in the queue and stops the pass rather than
   * dropping it or hammering: whatever broke will still be broken for the next
   * one, and the retry costs nothing but a later tick.
   */
  const drain = useCallback(async () => {
    if (draining.current) return
    draining.current = true
    try {
      /*
       * Re-read storage EVERY pass, and remove only the exact item that was
       * sent. Holding a snapshot across an await and writing it back is the
       * lost-update race: a tap landing mid-request was never in the snapshot,
       * so writing the snapshot back deleted it.
       */
      let n = 0
      for (;;) {
        const pending = loadQueue(store)
        const item = pending[0]
        if (!item) break
        try {
          await api.pick(item.gameId, item.playerId, item.side)
        } catch (e) {
          /*
           * A refusal is not a network problem. Keeping it meant retrying it
           * every four seconds and going on DRAWING it as a saved pick - so a
           * tap a minute after the deadline looked saved forever. Drop it, say
           * so, and let the server's own answer show through again.
           */
          if (e instanceof ApiError && permanent(e.status)) {
            saveQueue(store, removeSynced(loadQueue(store), item))
            setQueue(loadQueue(store))
            setRefused(e.message)
            continue
          }
          setOffline(true)
          return
        }
        n += 1
        setSent((prev) => [...prev.filter((p) => p.gameId !== item.gameId), item])
        const left = removeSynced(loadQueue(store), item)
        saveQueue(store, left)
        setQueue(left)
      }
      setOffline(false)
      /*
       * Reconcile only if something was actually sent. The heartbeat runs
       * every four seconds whether or not anything is pending, and calling
       * this unconditionally refetched the entire state on every tick for a
       * queue that was already empty.
       */
      if (n > 0) onChanged()
    } finally {
      draining.current = false
    }
  }, [store, onChanged])

  /* Forget a sent pick as soon as the state we are holding agrees with it. */
  useEffect(() => {
    setSent((prev) => {
      const next = confirmed(prev, state.picks, state.me.playerId ?? '')
      return next.length === prev.length ? prev : next
    })
  }, [state.picks, state.me.playerId])

  // A slow heartbeat, plus the moment the browser says the network is back.
  useEffect(() => {
    void drain()
    const t = setInterval(() => void drain(), 4000)
    const back = () => void drain()
    window.addEventListener('online', back)
    return () => { clearInterval(t); window.removeEventListener('online', back) }
  }, [drain])

  /*
   * SHUT EACH CARD AT ITS OWN KICKOFF, not at the next refetch.
   *
   * The poll is two minutes, so a phone sitting open through a kickoff went on
   * offering taps the server would refuse. This re-renders once, at the next
   * kickoff that has not happened yet, and re-arms for the one after it -
   * ten games over four days need ten of these, not one. The poll still
   * carries the server's own answer, which is what actually decides.
   */
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000))
  const nextKickoff = state.games.reduce<number | null>(
    (soonest, g) => (g.kickoffAt > now && (soonest === null || g.kickoffAt < soonest) ? g.kickoffAt : soonest),
    null,
  )
  useEffect(() => {
    if (nextKickoff === null) return
    const ms = nextKickoff * 1000 - Date.now()
    if (ms <= 0) return
    const id = setTimeout(() => setNow(Math.floor(Date.now() / 1000)), ms)
    return () => clearTimeout(id)
  }, [nextKickoff])

  const heldServer = (state.counts.find((c) => c.playerId === me)?.lockedAt ?? null) !== null
  // Drop the local answer once the server's agrees with it.
  useEffect(() => {
    if (heldLocal === heldServer) setHeldLocal(null)
  }, [heldLocal, heldServer])

  if (!state.week) return <Empty>No board is up yet. The commissioner sets one each week.</Empty>
  if (state.games.length === 0) return <Empty>This week’s board is empty.</Empty>
  if (!me) return <Empty>Pick a player first.</Empty>

  const mine = mergePicks(state.picks, queue, me, sent)
  const made = state.games.filter((g) => mine.has(g.id)).length
  const held = heldLocal ?? heldServer
  /* Shut when every game on the board has kicked off - there is then nothing
     left to lock, reopen, or nag anybody about. */
  const closed = state.games.every((g) => gameClosed(g.kickoffAt, now))

  /** Instant. No await, no spinner, no chance of a lost tap. */
  const pick = (gameId: string, side: Side) => {
    const g = state.games.find((x) => x.id === gameId)
    if (held || !g || gameClosed(g.kickoffAt, now)) return
    const next = enqueue(loadQueue(store), { gameId, playerId: me, side, at: Date.now() })
    saveQueue(store, next)
    setQueue(next)
    void drain()
  }

  const toggleLock = async () => {
    const next = !held
    setHeldLocal(next)
    try {
      await api.lock(state.week!.id, me, next)
    } catch {
      // Refused or offline: show the lock as it really stands.
      setHeldLocal(null)
      return
    }
    onChanged()
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 px-[18px] pb-3">
        <span className="min-w-0">
          <span className="slate-num block text-[10.5px] font-medium tracking-[0.1em] text-mute uppercase">
            {state.week.label.replace('Week', 'WK')} · {state.games.length} games · {made} picked
            {held && ' · locked'}
          </span>
          <WeekDates week={state.week} tz={tz} />
        </span>
        {/*
         * Said quietly and only when true. The picks are safe on the phone, so
         * this is information rather than an alarm - it must not look like the
         * work was lost, because it wasn't.
         */}
        {queue.length > 0 && (
          <span className="slate-num text-[10px] tracking-[0.06em] text-faint uppercase">
            {offline ? 'saved on this phone' : 'saving…'}
          </span>
        )}
      </div>

      {/*
       * A pick the server threw out. Said plainly and left up: the card has
       * already snapped back to what is actually saved, and a board that
       * changes under you with no explanation is how people stop trusting it.
       */}
      {refused && (
        <div className="mx-3 mb-2.5 border border-hairline bg-recessed px-3 py-2.5">
          <span className="slate-num text-[10px] font-bold tracking-[0.1em] text-mute uppercase">
            {refused}
          </span>
          <span className="slate-num mt-0.5 block text-[9.5px] tracking-[0.06em] text-mute uppercase">
            Your board is as it was &mdash; that tap did not save.
          </span>
        </div>
      )}

      {/* Cards stack with a 10px gap on a 12px rail, per the handoff. The
          gap is what stops ten games reading as one striped block. */}
      <ul className="flex flex-col gap-[10px] px-3 pb-[18px]">
        {state.games.map((g) => (
          <GameCard
            key={g.id}
            game={g}
            tz={tz}
            picked={mine.get(g.id) ?? null}
            onPick={(side) => pick(g.id, side)}
            held={held || gameClosed(g.kickoffAt, now)}
          />
        ))}
      </ul>

      {/* Past the deadline there is nothing left to lock or reopen, and a
          "tap to reopen" that reopens nothing is a lie. */}
      {!closed && (
        <div className="px-4 py-5">
          <button
            type="button"
            onClick={() => void toggleLock()}
            className={[
              'slate-tap w-full px-4 py-4 text-lg font-bold uppercase tracking-[0.04em]',
              held ? 'border border-hairline bg-card text-mute' : 'bg-ink text-card',
            ].join(' ')}
          >
            {held ? 'Locked — tap to reopen' : 'Lock it in'}
          </button>
          <p className="mt-3 text-center text-xs text-mute">
            {held
              ? 'Your picks are locked. Reopen to change them before the first kickoff.'
              : 'Picks save on your phone the instant you tap. Everything locks at the first kickoff.'}
          </p>
        </div>
      )}
    </div>
  )
}
